import { eligibleStaff, getSlots, nextFreeSlots, type SlotsResult } from '../availability';
import type { BookFailure, MoveFailure } from '../booking';
import { normalizeIsraeliPhone } from '../phone';
import {
  depositPaid, depositRule, enabledOnlineMethods, evaluateCancellation, evaluateNoShow, evaluateReschedule, holdExpiry,
  type CancelConsequence, type NoShowConsequence, type RescheduleCheck,
} from '../policies';
import { dayKeyOf, monthKeyOf, toMs, HOUR } from '../time';
import {
  fail, ok, paymentMethodsOf, type Appointment, type Business, type Client, type DayKey, type Id, type IsoUtc, type Message, type OnlineMethod, type PaymentMethod, type Result, type Service, type Settings,
  type Slot, type Staff,
} from '../types';
import { entryMatchesSlot } from '../waitlist';
import { BOOKING_STORES } from '../../adapters/types';
import { clientUseCases } from './clients';
import { availabilityData, firstName, listAppointments, loadBundle, loadBundleBySlug, loadPublicBundle, makeToken, notifyAppointment, type Bundle, type Deps } from './context';

export interface BookingPage {
  business: Pick<Business, 'id' | 'slug' | 'name' | 'profession' | 'brand' | 'about' | 'phone' | 'whatsapp' | 'address' | 'instagram' | 'locale' | 'tz'>;
  services: Service[];
  staff: Staff[];
  policy: Pick<Settings, 'cancelWindowH' | 'lateCancelFee' | 'noShowFee' | 'rescheduleWindowH' | 'leadTimeMin' | 'maxAdvanceDays'>;
  /** Earliest free slot of any service, for the "next free: tomorrow 10:00" shortcut. */
  nextFree?: Slot & { serviceId: Id };
  depositsEnabled: boolean;
  /** Online deposit methods the client may choose (empty: a service deposit is ignored). */
  depositMethods: OnlineMethod[];
  /** Cash at the venue is accepted. */
  acceptsCash: boolean;
  waitlistEnabled: boolean;
}

export interface BookAsClientInput {
  slug?: string;
  businessId?: Id;
  serviceId: Id;
  staffId: Id | 'any';
  start: IsoUtc;
  client: { name: string; phone: string };
  /** Complete desired consent state; never pre-ticked by the UI. */
  consents: { reminders: boolean; marketing: boolean };
  note?: string;
  /** For a deposit service: how the client pays. Defaults to the first enabled online method (card, then Bit, then PayBox). */
  depositMethod?: OnlineMethod;
}
export interface BookingConfirmation {
  appointment: Appointment;
  client: Client;
  service: Service;
  manageUrl: string;
  /** Present when a deposit holds the slot: send the customer to checkoutUrl. */
  payment?: { paymentId: Id; checkoutUrl: string; amountAgorot: number; holdExpiresAt: IsoUtc };
  /** Present for a Bit/PayBox deposit: show the owner's link/phone, then call claimDeposit when the client taps "I paid". */
  offlinePayment?: { method: 'bit' | 'paybox'; link?: string; phone?: string; amountAgorot: number; holdExpiresAt: IsoUtc };
  messages: Message[];
}
export type BookAsClientError =
  | BookFailure['reason'] | 'invalid_phone' | 'invalid_name' | 'plan_booking_limit' | 'payment_method_unavailable';

export interface BookAsOwnerInput {
  businessId: Id;
  clientId?: Id;
  newClient?: { name: string; phone: string };
  serviceId: Id;
  staffId: Id | 'any';
  start: IsoUtc;
  note?: string;
}

export interface MoveInput { appointmentId: Id; start: IsoUtc; staffId?: Id | 'any'; by: 'owner' | 'client'; token?: string;
  /** false = move now but do not send the 'rescheduled' message (owner undo window); send it later with `notifyRescheduled`. Default true. */
  notify?: boolean }
export type MoveError = MoveFailure['reason'] | 'forbidden' | 'not_active' | 'already_started' | 'inside_window';
export interface CancelInput { appointmentId: Id; by: 'owner' | 'client'; token?: string }
export interface OpenedSlot { start: IsoUtc; end: IsoUtc; staffId: Id; serviceId: Id }
export interface CancelOutcome {
  appointment: Appointment;
  consequence: CancelConsequence;
  /** The freed slot, when the cancelled appointment was in the future (offer it to the waitlist). */
  opened?: OpenedSlot & { waitingCount: number };
}

export interface ManageView {
  appointment: Appointment;
  service: Service;
  staff: Staff | undefined;
  business: BookingPage['business'];
  clientFirstName: string;
  state: 'upcoming' | 'past' | 'cancelled' | 'awaiting_deposit';
  /** What cancelling right now would cost; render before the confirm tap. */
  cancel: CancelConsequence;
  reschedule: RescheduleCheck;
  policy: BookingPage['policy'];
  manageUrl: string;
  /** While a Bit/PayBox deposit is awaited (awaiting_deposit, depositStatus required or claimed): where and how to pay. */
  offlinePayment?: NonNullable<BookingConfirmation['offlinePayment']> & { claimed: boolean };
}

/** Links expire this long after the appointment ended. */
export const MANAGE_LINK_GRACE_DAYS = 14;
const FREE_PLAN_COUNTED = new Set(['awaiting_deposit', 'pending', 'confirmed', 'completed', 'no_show']);

export function bookingUseCases(d: Deps) {
  const clients = clientUseCases(d);
  const now = (): IsoUtc => d.clock.nowIso();

  const publicBusiness = (b: Business): BookingPage['business'] => ({
    id: b.id, slug: b.slug, name: b.name, profession: b.profession, brand: b.brand, locale: b.locale, tz: b.tz,
    ...(b.about ? { about: b.about } : {}), ...(b.phone ? { phone: b.phone } : {}), ...(b.whatsapp ? { whatsapp: b.whatsapp } : {}),
    ...(b.address ? { address: b.address } : {}), ...(b.instagram ? { instagram: b.instagram } : {}),
  });
  const policyOf = (s: Settings): BookingPage['policy'] => ({
    cancelWindowH: s.cancelWindowH, lateCancelFee: s.lateCancelFee, noShowFee: s.noShowFee, rescheduleWindowH: s.rescheduleWindowH,
    leadTimeMin: s.leadTimeMin, maxAdvanceDays: s.maxAdvanceDays,
  });

  async function bundleFor(input: { slug?: string; businessId?: Id }): Promise<Bundle | undefined> {
    if (input.businessId) return loadPublicBundle(d, input.businessId);
    return input.slug ? loadBundleBySlug(d, input.slug) : undefined;
  }

  async function getBookingPage(slug: string): Promise<Result<BookingPage, 'not_found'>> {
    const b = await loadBundleBySlug(d, slug);
    if (!b) return fail('not_found');
    const appointments = await listAppointments(d, b.business.id);
    const services = b.services.filter((s) => s.active).sort((a, c) => a.sortOrder - c.sortOrder);
    const data = availabilityData(b, appointments);
    const today = dayKeyOf(d.clock.now(), b.business.tz);
    let nextFree: BookingPage['nextFree'];
    for (const service of services) {
      const slot = nextFreeSlots({ ...data, service, staffChoice: 'any', fromDay: today, now: d.clock.now() }, 1)[0];
      if (slot && (!nextFree || slot.start < nextFree.start)) nextFree = { ...slot, serviceId: service.id };
    }
    return ok({
      business: publicBusiness(b.business), services, staff: b.staff.filter((s) => s.active).sort((x, y) => x.sortOrder - y.sortOrder),
      policy: policyOf(b.settings), depositsEnabled: b.plan.caps.deposits && enabledOnlineMethods(b.settings).length > 0, depositMethods: enabledOnlineMethods(b.settings), acceptsCash: paymentMethodsOf(b.settings).cash, waitlistEnabled: b.plan.caps.waitlist,
      ...(nextFree ? { nextFree } : {}),
    });
  }

  async function getSlotsFor(input: { businessId: Id; serviceId: Id; staffId: Id | 'any'; fromDay: DayKey; toDay: DayKey }): Promise<Result<SlotsResult, 'business_not_found' | 'service_not_found'>> {
    const b = await loadPublicBundle(d, input.businessId);
    if (!b) return fail('business_not_found');
    const service = b.services.find((s) => s.id === input.serviceId && s.active);
    if (!service) return fail('service_not_found');
    return ok(getSlots({ ...availabilityData(b, await listAppointments(d, b.business.id)), service, staffChoice: input.staffId, fromDay: input.fromDay, toDay: input.toDay, now: d.clock.now() }));
  }

  /** Free plan: 30 bookings a month. */
  async function overBookingCap(b: Bundle): Promise<boolean> {
    const cap = b.plan.caps.monthlyBookingCap;
    if (cap === null) return false;
    const month = monthKeyOf(d.clock.now(), b.business.tz);
    const all = await listAppointments(d, b.business.id);
    return all.filter((a) => FREE_PLAN_COUNTED.has(a.status) && monthKeyOf(toMs(a.createdAt), b.business.tz) === month).length >= cap;
  }

  /** Creates the mock checkout for a held appointment and records it on the appointment. */
  async function attachDeposit(b: Bundle, appointment: Appointment, amountAgorot: number): Promise<{ appointment: Appointment; payment: NonNullable<BookingConfirmation['payment']> }> {
    const created = await d.payments.createDeposit({ businessId: b.business.id, appointmentId: appointment.id, amountAgorot, holdMinutes: b.settings.depositHoldMin });
    const next: Appointment = { ...appointment, paymentId: created.paymentId, updatedAt: now() };
    await d.storage.repo('appointments').put(next);
    return { appointment: next, payment: { paymentId: created.paymentId, checkoutUrl: created.checkoutUrl, amountAgorot, holdExpiresAt: appointment.holdExpiresAt ?? now() } };
  }

  async function bookAsClient(input: BookAsClientInput): Promise<Result<BookingConfirmation, BookAsClientError>> {
    const b = await bundleFor(input);
    if (!b) return fail('business_not_found');
    const service = b.services.find((s) => s.id === input.serviceId);
    if (!service) return fail('service_not_found');
    const phone = normalizeIsraeliPhone(input.client.phone);
    if (!phone.ok || phone.kind !== 'mobile') return fail('invalid_phone');
    if (input.client.name.trim().length < 2) return fail('invalid_name');
    if (await overBookingCap(b)) return fail('plan_booking_limit');

    const { client } = await clients.findOrCreate(b.business.id, input.client.name, phone.e164);
    const deposit = depositRule(service, b.settings, b.plan.caps);
    const methods = enabledOnlineMethods(b.settings);
    const method: OnlineMethod | undefined = deposit.required ? (input.depositMethod ?? methods[0]) : undefined;
    if (deposit.required && (!method || !methods.includes(method))) return fail('payment_method_unavailable');
    const t = d.clock.now();
    const booked = await d.storage.bookAppointment({
      businessId: b.business.id, clientId: client.id, serviceId: service.id, staffId: input.staffId, start: input.start, source: 'link',
      status: deposit.required ? 'awaiting_deposit' : b.settings.approvalMode === 'manual' ? 'pending' : 'confirmed',
      manageToken: makeToken(), priceAgorot: service.priceAgorot, depositAgorot: deposit.amountAgorot,
      depositStatus: deposit.required ? 'required' : 'none', ...(deposit.required ? { holdExpiresAt: holdExpiry(t, b.settings) } : {}),
      ...(method ? { depositMethod: method } : {}),
      ...(input.note ? { note: input.note } : {}),
    }, now());
    if (!booked.ok) return fail(booked.reason, booked.reason === 'conflict' ? { nextSlots: booked.nextSlots } : undefined);

    await clients.syncConsents(client, { reminders: input.consents.reminders, marketing: input.consents.marketing }, 'booking_page');
    let appointment = booked.appointment;
    let payment: BookingConfirmation['payment'];
    let messages: Message[];
    let offlinePayment: BookingConfirmation['offlinePayment'];
    if (deposit.required && method === 'card') {
      ({ appointment, payment } = await attachDeposit(b, appointment, deposit.amountAgorot));
      messages = await notifyAppointment(d, 'deposit_requested', appointment, { payUrl: d.links.pay(payment.paymentId) });
    } else if (deposit.required && (method === 'bit' || method === 'paybox')) {
      const cfg = paymentMethodsOf(b.settings)[method];
      offlinePayment = { method, ...(cfg.link ? { link: cfg.link } : {}), ...(cfg.phone ? { phone: cfg.phone } : {}), amountAgorot: deposit.amountAgorot, holdExpiresAt: appointment.holdExpiresAt ?? now() };
      messages = await notifyAppointment(d, 'deposit_requested', appointment, { payUrl: cfg.link ?? d.links.manage(appointment.manageToken) });
    } else {
      messages = await notifyAppointment(d, 'booked', appointment);
    }
    return ok({ appointment, client, service, manageUrl: d.links.manage(appointment.manageToken), messages, ...(payment ? { payment } : {}), ...(offlinePayment ? { offlinePayment } : {}) });
  }

  async function bookAsOwner(input: BookAsOwnerInput): Promise<Result<{ appointment: Appointment; client: Client; messages: Message[] }, BookFailure['reason'] | 'invalid_phone' | 'invalid_name' | 'plan_booking_limit'>> {
    const b = await loadBundle(d, input.businessId);
    if (!b) return fail('business_not_found');
    const service = b.services.find((s) => s.id === input.serviceId);
    if (!service) return fail('service_not_found');
    let client: Client | undefined;
    if (input.clientId) client = await d.storage.repo('clients').get(input.clientId);
    else if (input.newClient) {
      const created = await clients.upsertClient({ businessId: b.business.id, ...input.newClient });
      if (!created.ok) return fail(created.reason === 'invalid_phone' ? 'invalid_phone' : created.reason === 'invalid_name' ? 'invalid_name' : 'client_not_found');
      client = created.value.client;
    }
    if (!client || client.businessId !== b.business.id) return fail('client_not_found');
    if (await overBookingCap(b)) return fail('plan_booking_limit');
    const booked = await d.storage.bookAppointment({
      businessId: b.business.id, clientId: client.id, serviceId: service.id, staffId: input.staffId, start: input.start, source: 'owner',
      status: 'confirmed', manageToken: makeToken(), priceAgorot: service.priceAgorot, depositAgorot: 0, depositStatus: 'none',
      ...(input.note ? { note: input.note } : {}),
    }, now());
    if (!booked.ok) return fail(booked.reason, booked.reason === 'conflict' ? { nextSlots: booked.nextSlots } : undefined);
    return ok({ appointment: booked.appointment, client, messages: await notifyAppointment(d, 'booked', booked.appointment) });
  }

  // ------------------------------------------------------------ change / cancel

  async function authorise(appointmentId: Id, by: 'owner' | 'client', token?: string): Promise<Appointment | 'appointment_not_found' | 'forbidden'> {
    const a = await d.storage.repo('appointments').get(appointmentId);
    if (!a) return 'appointment_not_found';
    return by === 'client' && (!token || token !== a.manageToken) ? 'forbidden' : a;
  }

  async function moveAppointment(input: MoveInput): Promise<Result<{ appointment: Appointment; previousStart: IsoUtc; messages: Message[] }, MoveError>> {
    const a = await authorise(input.appointmentId, input.by, input.token);
    if (typeof a === 'string') return fail(a);
    const b = await loadBundle(d, a.businessId);
    if (!b) return fail('business_not_found');
    const rule = evaluateReschedule({ appointment: a, settings: b.settings, nowMs: d.clock.now(), by: input.by });
    if (!rule.allowed) return fail(rule.reason);
    if (input.start === a.start && (!input.staffId || input.staffId === a.staffId)) return ok({ appointment: a, previousStart: a.start, messages: [] });
    const moved = await d.storage.moveAppointment({ appointmentId: a.id, start: input.start, source: input.by === 'owner' ? 'owner' : 'link', ...(input.staffId ? { staffId: input.staffId } : {}) }, now());
    if (!moved.ok) return fail(moved.reason, moved.reason === 'conflict' ? { nextSlots: moved.nextSlots } : undefined);
    const messages = input.notify === false ? [] : await notifyAppointment(d, 'rescheduled', moved.appointment, { previousStart: a.start });
    return ok({ appointment: moved.appointment, previousStart: a.start, messages });
  }

  /** Sends the deferred 'rescheduled' message for a move made with `notify: false`. No-op (empty list) if the appointment is no longer active or is back at `previousStart`. */
  async function notifyRescheduled(input: { appointmentId: Id; previousStart: IsoUtc }): Promise<Result<{ messages: Message[] }, 'appointment_not_found' | 'not_active'>> {
    const a = await d.storage.repo('appointments').get(input.appointmentId);
    if (!a) return fail('appointment_not_found');
    if (!['pending', 'confirmed', 'awaiting_deposit'].includes(a.status)) return fail('not_active');
    if (a.start === input.previousStart) return ok({ messages: [] });
    return ok({ messages: await notifyAppointment(d, 'rescheduled', a, { previousStart: input.previousStart }) });
  }

  async function waitingFor(b: Bundle, slot: OpenedSlot): Promise<number> {
    const entries = await d.storage.repo('waitlistEntries').list({ where: { businessId: b.business.id, status: 'waiting' } });
    return entries.filter((e) => entryMatchesSlot(e, slot, b.business.tz)).length;
  }

  async function cancelAppointment(input: CancelInput): Promise<Result<CancelOutcome, 'appointment_not_found' | 'forbidden' | 'not_active' | 'already_started' | 'business_not_found'>> {
    const found = await authorise(input.appointmentId, input.by, input.token);
    if (typeof found === 'string') return fail(found);
    const b = await loadBundle(d, found.businessId);
    if (!b) return fail('business_not_found');
    const consequence = evaluateCancellation({ appointment: found, settings: b.settings, nowMs: d.clock.now(), by: input.by });
    if (!consequence.allowed) return fail(consequence.deniedReason ?? 'not_active');

    const t = now();
    const cancelled = await d.storage.transact(['appointments'], async (tx) => {
      const cur = await tx.get('appointments', found.id);
      if (!cur || !['awaiting_deposit', 'pending', 'confirmed'].includes(cur.status)) return undefined;
      const next: Appointment = {
        ...cur, status: input.by === 'owner' ? 'cancelled_owner' : 'cancelled_client', cancelledAt: t, cancelReason: input.by,
        ...(consequence.feeAgorot > 0 ? { feeAgorot: consequence.feeAgorot } : {}), updatedAt: t,
      };
      await tx.put('appointments', next);
      return next;
    });
    if (!cancelled) return fail('not_active');

    let appointment = cancelled;
    if (found.paymentId) {
      if (found.status === 'awaiting_deposit') await d.payments.complete(found.paymentId, 'failure');
      else if (consequence.refundAgorot > 0) await d.payments.refund(found.paymentId, consequence.refundAgorot);
      if (consequence.refundAgorot > 0 && consequence.refundAgorot === depositPaid(found)) {
        appointment = { ...cancelled, depositStatus: 'refunded', updatedAt: now() };
        await d.storage.repo('appointments').put(appointment);
      }
    }
    await notifyAppointment(d, input.by === 'owner' ? 'cancelled_by_owner' : 'cancelled_by_client', appointment);

    const outcome: CancelOutcome = { appointment, consequence };
    if (toMs(appointment.start) > d.clock.now()) {
      const slot: OpenedSlot = { start: appointment.start, end: appointment.end, staffId: appointment.staffId, serviceId: appointment.serviceId };
      const waitingCount = await waitingFor(b, slot);
      outcome.opened = { ...slot, waitingCount };
    }
    return ok(outcome);
  }

  /** Undo for a just-made cancellation: puts the appointment back if its slot is still free. */
  async function undoCancel(appointmentId: Id): Promise<Result<Appointment, 'appointment_not_found' | 'not_cancelled' | 'refunded' | 'conflict' | 'business_not_found'>> {
    const cur = await d.storage.repo('appointments').get(appointmentId);
    if (!cur) return fail('appointment_not_found');
    if (cur.status !== 'cancelled_owner' && cur.status !== 'cancelled_client') return fail('not_cancelled');
    if (cur.depositStatus === 'refunded') return fail('refunded');
    const b = await loadBundle(d, cur.businessId);
    if (!b) return fail('business_not_found');
    const restored = await d.storage.transact(BOOKING_STORES, async (tx) => {
      const live = await tx.get('appointments', appointmentId);
      if (!live) return undefined;
      const clash = (await tx.list('appointments', { where: { businessId: live.businessId }, range: { field: 'start', gte: new Date(toMs(live.start) - 48 * HOUR).toISOString() } }))
        .some((o) => o.id !== live.id && ['pending', 'confirmed', 'completed', 'no_show', 'awaiting_deposit'].includes(o.status) && o.staffId === live.staffId
          && toMs(live.start) < toMs(o.blockedUntil) && toMs(o.start) < toMs(live.blockedUntil));
      if (clash) return 'conflict' as const;
      const { cancelledAt: _c, cancelReason: _r, feeAgorot: _f, ...rest } = live;
      const next: Appointment = { ...rest, status: b.settings.approvalMode === 'manual' ? 'pending' : 'confirmed', updatedAt: now() };
      await tx.put('appointments', next);
      return next;
    });
    if (restored === 'conflict') return fail('conflict');
    if (!restored) return fail('appointment_not_found');
    for (const m of await d.messaging.listOutbox({ appointmentId, status: 'queued' })) {
      if (m.template.startsWith('cancelled_')) await d.messaging.cancel(m.id);
    }
    await notifyAppointment(d, 'booked', restored);
    return ok(restored);
  }

  async function setStatus(appointmentId: Id, allowed: Appointment['status'][], patch: (a: Appointment) => Partial<Appointment>): Promise<Result<Appointment, 'appointment_not_found' | 'not_active'>> {
    const t = now();
    const result = await d.storage.transact(['appointments'], async (tx) => {
      const cur = await tx.get('appointments', appointmentId);
      if (!cur) return 'appointment_not_found' as const;
      if (!allowed.includes(cur.status)) return 'not_active' as const;
      const next = { ...cur, ...patch(cur), updatedAt: t };
      await tx.put('appointments', next);
      return next;
    });
    return typeof result === 'string' ? fail(result) : ok(result);
  }

  const completeAppointment = (id: Id) => setStatus(id, ['pending', 'confirmed'], () => ({ status: 'completed' as const }));

  async function markNoShow(id: Id): Promise<Result<{ appointment: Appointment; consequence: NoShowConsequence }, 'appointment_not_found' | 'not_active'>> {
    const cur = await d.storage.repo('appointments').get(id);
    const settings = cur ? await d.storage.repo('settings').get(cur.businessId) : undefined;
    if (!cur || !settings) return fail('appointment_not_found');
    const consequence = evaluateNoShow(cur, settings);
    const r = await setStatus(id, ['pending', 'confirmed'], () => ({ status: 'no_show' as const, ...(consequence.feeAgorot > 0 ? { feeAgorot: consequence.feeAgorot } : {}) }));
    if (!r.ok) return r;
    for (const m of await d.messaging.listOutbox({ appointmentId: id, status: 'queued' })) await d.messaging.cancel(m.id);
    return ok({ appointment: r.value, consequence });
  }

  /**
   * Owner records what was collected at the venue (cash / Bit / PayBox / card / other) and completes the appointment.
   * Amount defaults to the balance (price minus a paid deposit). Legacy call form `markPaid(id, { amountAgorot })` still works (method 'other').
   */
  async function markPaid(id: Id, methodOrOpts: PaymentMethod | { amountAgorot?: number; method?: PaymentMethod } = {}, amountAgorot?: number): Promise<Result<Appointment, 'appointment_not_found' | 'not_active' | 'invalid_amount'>> {
    const opts = typeof methodOrOpts === 'string' ? { method: methodOrOpts, amountAgorot } : { method: methodOrOpts.method, amountAgorot: methodOrOpts.amountAgorot ?? amountAgorot };
    if (opts.amountAgorot !== undefined && (!Number.isInteger(opts.amountAgorot) || opts.amountAgorot < 0)) return fail('invalid_amount');
    const t = now();
    return setStatus(id, ['pending', 'confirmed', 'completed'], (a) => {
      const paid = opts.amountAgorot ?? Math.max(0, a.priceAgorot - depositPaid(a));
      return { status: 'completed' as const, paidAt: t, paidAgorot: paid, payment: { method: opts.method ?? 'other', paidAgorot: paid, paidAt: t } };
    });
  }

  // ------------------------------------------------------------ customer manage link

  async function getManageView(token: string): Promise<Result<ManageView, 'not_found' | 'expired'>> {
    const [a] = await d.storage.repo('appointments').list({ where: { manageToken: token } });
    if (!a || token === '') return fail('not_found');
    if (d.clock.now() > toMs(a.end) + MANAGE_LINK_GRACE_DAYS * 24 * HOUR) return fail('expired');
    const b = await loadBundle(d, a.businessId);
    const service = b?.services.find((s) => s.id === a.serviceId);
    const client = await d.storage.repo('clients').get(a.clientId);
    if (!b || !service) return fail('not_found');
    const nowMs = d.clock.now();
    const state = a.status === 'cancelled_client' || a.status === 'cancelled_owner' ? 'cancelled'
      : a.status === 'awaiting_deposit' ? 'awaiting_deposit' : toMs(a.end) <= nowMs ? 'past' : 'upcoming';
    return ok({
      appointment: a, service, staff: b.staff.find((s) => s.id === a.staffId), business: publicBusiness(b.business),
      clientFirstName: client ? firstName(client.name) : '', state,
      cancel: evaluateCancellation({ appointment: a, settings: b.settings, nowMs, by: 'client' }),
      reschedule: evaluateReschedule({ appointment: a, settings: b.settings, nowMs, by: 'client' }),
      policy: policyOf(b.settings), manageUrl: d.links.manage(token),
      ...(a.status === 'awaiting_deposit' && (a.depositMethod === 'bit' || a.depositMethod === 'paybox') ? (() => {
        const cfg = paymentMethodsOf(b.settings)[a.depositMethod];
        return { offlinePayment: { method: a.depositMethod, ...(cfg.link ? { link: cfg.link } : {}), ...(cfg.phone ? { phone: cfg.phone } : {}), amountAgorot: a.depositAgorot, holdExpiresAt: a.holdExpiresAt ?? a.end, claimed: a.depositStatus === 'claimed' } };
      })() : {}),
    });
  }

  /** "Find my booking by phone": step 1, sends the mock code (read it with auth.getDemoCode). */
  const requestBookingLookup = (phone: string) => d.auth.requestOtp(phone);

  async function lookupBookingsByPhone(input: { slug: string; phone: string; code: string }): Promise<Result<Array<{ appointment: Appointment; service: Service; manageUrl: string }>, 'not_found' | 'invalid_phone' | 'no_code' | 'wrong_code' | 'expired' | 'too_many_attempts'>> {
    const b = await loadBundleBySlug(d, input.slug);
    if (!b) return fail('not_found');
    const verified = await d.auth.verifyOtp(input.phone, input.code, { persist: false });
    if (!verified.ok) return fail(verified.reason);
    const [client] = await d.storage.repo('clients').list({ where: { phone: verified.session.phone }, filter: (c) => c.businessId === b.business.id && !c.deletedAt });
    if (!client) return ok([]);
    const nowMs = d.clock.now();
    const upcoming = (await d.storage.repo('appointments').list({ where: { clientId: client.id } }))
      .filter((a) => toMs(a.end) > nowMs && ['pending', 'confirmed', 'awaiting_deposit'].includes(a.status))
      .sort((x, y) => x.start.localeCompare(y.start));
    return ok(upcoming.flatMap((appointment) => {
      const service = b.services.find((s) => s.id === appointment.serviceId);
      return service ? [{ appointment, service, manageUrl: d.links.manage(appointment.manageToken) }] : [];
    }));
  }

  /** Staff members able to take a service, for the optional "choose a person" step. */
  async function staffForService(businessId: Id, serviceId: Id): Promise<Staff[]> {
    const b = await loadBundle(d, businessId);
    const service = b?.services.find((s) => s.id === serviceId);
    return b && service ? eligibleStaff(service, b.staff, 'any') : [];
  }

  return {
    getBookingPage, getSlots: getSlotsFor, bookAsClient, bookAsOwner, moveAppointment, notifyRescheduled, cancelAppointment, undoCancel,
    completeAppointment, markNoShow, markPaid, getManageView, requestBookingLookup, lookupBookingsByPhone, staffForService,
  };
}
